import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid2'
import { allocationAPI } from 'api/AllocationAPI'
import colors from 'constants/colors'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IAllocation } from 'types/assignmentInterfaces'
import { convertToDisplayDate } from 'utils/utils'
import CardContentText from '../CaleoCustomComponents/CardContentText'
import { CustomTooltip } from '../CaleoCustomComponents/CustomTooltip'
import WarningIcon from '@mui/icons-material/Warning'
import { add, startOfMonth, format, isAfter, isBefore, isEqual, sub } from 'date-fns'
import { enGB } from 'date-fns/locale'
import { fi } from 'date-fns/locale'
import { IError } from 'types/error'
import ErrorOverlay from 'Components/General/ErrorOverlay'

/** @notExported  */
interface IAllocationTimelineProps {
  /** Person ID */
  personId: number | string
  /** Allocations data */
  data?: IAllocation[] | null
  /** Is role view? (default: false) */
  isRoleView?: boolean
}

/**
 * Allocation timeline for person.
 *
 * @returns Allocation timeline component
 * @notExported
 */
const AllocationTimeline: React.FC<IAllocationTimelineProps> = ({ personId, data, isRoleView = false }) => {
  const [allocations, setAllocations] = useState<IAllocation[]>([])
  const { t, i18n } = useTranslation()
  const [loading, setLoading] = useState<boolean>(true)
  const [backendError, setBackendError] = useState<IError>()

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    ;(async () => {
      try {
        setLoading(true)
        if (personId && typeof personId === 'number') {
          if (!data || isRoleView === true) {
            // teams view
            const results = await allocationAPI.getAllocationsForTimeline(personId, controller)
            if (!cancelled) {
              setAllocations(results)
            }
          } else {
            // search view
            if (!cancelled) {
              setAllocations(data)
            }
          }
        } else if (personId && typeof personId === 'string') {
          const results = await allocationAPI.getAllocationsForPublicIdForTimeline(personId, controller)
          if (!cancelled) {
            setAllocations(results)
          }
        } else {
          if (!cancelled) {
            setAllocations([])
          }
        }
      } catch (error) {
        if (!cancelled) {
          setBackendError(error as IError)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [data, isRoleView, personId])

  // Make the array of dates from first day of the current month until the first day of the next years current month.
  const getDatesBetween = (startDate: Date, endDate: Date) => {
    const dates: Date[] = []
    let theDate = new Date(startDate)
    while (theDate < endDate) {
      dates.push(new Date(theDate))
      theDate = add(theDate, { days: 1 })
    }
    return dates
  }

  const currentYear = new Date().getFullYear()

  const startOfCurrentMonth = startOfMonth(new Date())
  const plusOneYear = add(startOfCurrentMonth, { years: 1 })

  const datesArray = getDatesBetween(startOfCurrentMonth, plusOneYear)

  const dayBoxWidth = 100 / datesArray.length

  const getBox = (boxColor: string, boxState: 0 | 1, dayCount: number, date: Date) => {
    const endDate = convertToDisplayDate(date, 'day')
    const startDate = convertToDisplayDate(sub(date, { days: dayCount + 1 }), 'day')
    let redStripes = false
    let showStripes = true
    const pendingText = (
      <Grid container alignItems="center">
        <Grid>
          <WarningIcon fontSize="small" />
        </Grid>
        <Grid>
          <CardContentText type="tooltip">{t('allocation.pending')}</CardContentText>
        </Grid>
      </Grid>
    )
    const boxTitle = () => {
      if (boxColor === colors.background) {
        showStripes = false
        return <CardContentText type="tooltip" fontWeight="bold">{`0%`}</CardContentText>
      } else if (boxColor === colors.orange) {
        return (
          <>
            {boxState < 1 && pendingText}
            <CardContentText type="tooltip">
              {startDate} - {endDate}
            </CardContentText>
            <CardContentText type="tooltip" fontWeight="bold">{`, <100%`}</CardContentText>
          </>
        )
      } else {
        redStripes = true
        return (
          <>
            {boxState < 1 && pendingText}
            <CardContentText type="tooltip">
              {startDate} - {endDate}
            </CardContentText>
            <CardContentText type="tooltip" fontWeight="bold">{`, 100%`}</CardContentText>
          </>
        )
      }
    }

    return (
      <CustomTooltip title={boxTitle()} key={convertToDisplayDate(date, 'day')}>
        <Grid
          aria-label="allocation"
          role="img"
          style={{
            background: boxState === 1 ? boxColor : 'undefined',
            minWidth: `${dayCount * dayBoxWidth}%`,
          }}
          sx={
            showStripes
              ? {
                  ...(redStripes
                    ? {
                        backgroundImage: theme =>
                          `linear-gradient(135deg,${theme.palette.error.main} 12.50%, #ffffff 12.50%, #ffffff 50%, ${theme.palette.error.main} 50%, ${theme.palette.error.main} 62.50%, #ffffff 62.50%, #ffffff 100%)`,
                        backgroundSize: `5.66px 5.66px`,
                      }
                    : {
                        backgroundImage: theme =>
                          `linear-gradient(135deg,${theme.palette.warning.main} 12.50%, #ffffff 12.50%, #ffffff 50%, ${theme.palette.warning.main} 50%, ${theme.palette.warning.main} 62.50%, #ffffff 62.50%, #ffffff 100%)`,
                        backgroundSize: `5.66px 5.66px`,
                      }),
                }
              : {}
          }
        />
      </CustomTooltip>
    )
  }

  type AllocationUnit = {
    allocationPercent: number
    date: Date
    allocationState: number
  }

  const getPercentArray = (datesArray: Date[], allocations: IAllocation[]): AllocationUnit[] => {
    const allocationPercentArray: AllocationUnit[] = []
    datesArray.forEach(date => {
      const thisDateAllocations = allocations.filter(
        allocation =>
          ((isBefore(allocation.startDate, date) || isEqual(date, allocation.startDate)) &&
            (isAfter(allocation.endDate, date) || isEqual(date, allocation.endDate))) ||
          ((isBefore(allocation.startDate, date) || isEqual(date, allocation.startDate)) && allocation.endDate === null)
      )
      const allocationPercent = thisDateAllocations.reduce((sum, current) => sum + current.percent, 0)
      const allocationState = thisDateAllocations.reduce((sum, current) => sum + current.state, 0)
      allocationPercentArray.push({ allocationPercent, date, allocationState })
    })
    return allocationPercentArray
  }

  const getMonths = (dates: Date[]) => {
    let currentMonth = new Date().getMonth()
    return dates.map((date: Date, index: number) => {
      const currentDate = new Date(currentYear, currentMonth, 1)
      if (currentDate.getDate() === date.getDate() && currentDate.getMonth() === date.getMonth()) {
        const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
        currentMonth += 1
        return (
          <Grid key={index} borderLeft={1} style={{ width: `${daysInMonth * dayBoxWidth}%` }}>
            <Grid container alignItems="flex-end" style={{ fontSize: 12, marginLeft: 2, height: 15 }}>
              <Typography textTransform="none" fontSize="12px">
                {format(new Date(currentYear, currentMonth - 1, 1), 'LLL', {
                  locale: i18n.language === 'fi' ? fi : enGB,
                })}
              </Typography>
            </Grid>
          </Grid>
        )
      }
    })
  }

  const getColor = (percentValue: number): string => {
    if (percentValue === 0) {
      return colors.background
    } else if (percentValue < 100) {
      return colors.orange
    } else {
      return colors.red
    }
  }

  const allocationsIndicator: React.ReactElement[] = []
  if (allocations.length > 0) {
    let prevColor: string | undefined
    let prevState: 0 | 1 | undefined
    let boxCount = 0
    let endDate: Date | undefined

    // Get the array of dates between the first day of the current month
    // and the first day of the next years current month
    const percentArray = getPercentArray(datesArray, allocations)

    // Create the array of boxes for the allocation timeline
    for (const unit of percentArray) {
      const currentColor = getColor(unit.allocationPercent)
      const currentState = unit.allocationState > 0 ? 1 : 0
      if (!prevColor || (prevColor === currentColor && prevState === currentState)) {
        boxCount += 1
        endDate = unit.date
      } else {
        if (prevState !== undefined && endDate !== undefined) {
          allocationsIndicator.push(getBox(prevColor, prevState, boxCount, endDate))
        }
        endDate = unit.date
        boxCount = 1
      }
      prevState = currentState
      prevColor = currentColor
    }

    if (prevColor !== undefined && prevState !== undefined && endDate !== undefined) {
      allocationsIndicator.push(getBox(prevColor, prevState, boxCount, endDate))
    }
  } else {
    allocationsIndicator.push(
      <CustomTooltip
        style={{ width: `${dayBoxWidth * datesArray.length}%` }}
        title={<CardContentText type="tooltip" fontWeight="bold">{`0%`}</CardContentText>}
        key={1}
      >
        <Grid
          aria-label="allocation"
          role="img"
          style={{
            background: colors.borders,
            minWidth: `${dayBoxWidth * datesArray.length}%`,
          }}
        />
      </CustomTooltip>
    )
  }

  if (loading) {
    return (
      <Grid container justifyContent="center" style={{ margin: 10 }}>
        <CircularProgress aria-label={t('loading')} />
      </Grid>
    )
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <Grid
      sx={{
        marginTop: '10px',
        marginBottom: '10px',
        '&:hover': {
          '& .allocation-indicator': {
            opacity: 1,
          },
        },
        width: '100%',
      }}
    >
      <Grid
        className="allocation-indicator"
        container
        style={{ height: 15, background: colors.background, opacity: 0.75 }}
      >
        {allocationsIndicator}
      </Grid>
      <Grid container>{getMonths(datesArray)}</Grid>
      <Grid container direction="row" justifyContent="space-between" style={{ fontSize: 10 }}>
        <Grid>{convertToDisplayDate(startOfCurrentMonth)}</Grid>
        <Grid>{convertToDisplayDate(plusOneYear)}</Grid>
      </Grid>
    </Grid>
  )
}

export default React.memo(AllocationTimeline)
